import axios from 'axios';
import { DossierId } from '@/dossier/models/AbstractDossier';
import { DateTimeFormat, formatMoment } from '@/shared/converters/dateConverters';
import { present } from '@/shared/utils/typescript';
import WorkPreparationStatus from '../constants/WorkPreparationStatus';
import { parseWorkPreparationSearchResultContainer } from '../converters/WorkPreparationSearchResultConverters';
import { formatWorkPreparationItem, parseWorkPreparationItem, parseWorkPreparationItems, parseWorkPreparationReasons } from '../converters/workPreparationConverters';
import WorkPreparationData from '../models/WorkPreparationData';
import WorkPreparationFilters from '../models/WorkPreparationFilters';
import WorkPreparationItem, { WorkPreparationItemId } from '../models/WorkPreparationItem';
import WorkPreparationReason from '../models/WorkPreparationReason';
import WorkPreparationSearchResultContainer from '../models/WorkPreparationSearchResultContainer';

export interface WorkPreparationSaveResult {
  item: WorkPreparationItem;
  status: WorkPreparationStatus;
}

export default class WorkPreparationClient {
  static async loadAll(dossierId: DossierId): Promise<Array<WorkPreparationItem>> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/workpreparations`);
    return parseWorkPreparationItems(response.data);
  }

  static async loadReasons(): Promise<Array<WorkPreparationReason>> {
    const response = await axios.get('/api/mobopro/v1/workpreparations/reasons');
    return parseWorkPreparationReasons(response.data);
  }

  static async search(filters: WorkPreparationFilters): Promise<WorkPreparationSearchResultContainer> {
    const params = {
      moId: filters.commonFilters.activeOutletIds[0],
      brandId: filters.commonFilters.activeBrandId,
      plannedFrom: formatMoment(filters.plannedFrom, DateTimeFormat.ROUTE_DATE_TIME),
      plannedTill: formatMoment(filters.plannedTill.clone().add(1, 'day'), DateTimeFormat.ROUTE_DATE_TIME),
      includePassed: filters.options.includePassed,
      myDossiers: filters.options.myDossiers,
      isCreationDate: filters.options.isCreationDate,
      teamId: filters.commonFilters.activeTeamId,
    };

    const response = await axios.get('/api/mobopro/v1/dossiers/workpreparations', { params });
    return present(parseWorkPreparationSearchResultContainer(response.data));
  }

  static async create(data: WorkPreparationData, dossierId: DossierId): Promise<WorkPreparationSaveResult> {
    const apiData = {
      ...formatWorkPreparationItem(data),
      dossierId,
    };
    const response = await axios.post('/api/mobopro/v1/dossiers/workpreparations', apiData);
    const item = present(parseWorkPreparationItem(response.data));
    return { item, status: response.data.workPreparationStatus };
  }

  static async update(id: WorkPreparationItemId, data: WorkPreparationData): Promise<WorkPreparationSaveResult> {
    const apiData = {
      ...formatWorkPreparationItem(data),
      id,
    };
    const response = await axios.put('/api/mobopro/v1/dossiers/workpreparations', apiData);
    const item = present(parseWorkPreparationItem(response.data));
    return { item, status: response.data.workPreparationStatus };
  }

  static async remove(id: WorkPreparationItemId): Promise<WorkPreparationStatus> {
    const response = await axios.delete(`/api/mobopro/v1/dossiers/workpreparations/${id}`);
    return response.data.workPreparationStatus;
  }
}
